import React, { useState, useMemo } from 'react';
import { RecommendationHeader } from './header/RecommendationHeader';
import { TicketMetricsSection } from './TicketMetrics/TicketMetricsSection';
import { EffortTypesSection } from './EffortTypes/EffortTypesSection';
import { TicketDetailsSection } from './TicketDetails/TicketDetailsSection';
import { 
  TICKET_METRICS_DATA, 
  EFFORT_TYPES_DATA, 
  TICKET_DETAILS_DATA, 
  FILTER_OPTIONS 
} from './mockData';
import styles from './RecommendationCenter.module.css';

const RecommendationCenter = () => {
  // State for filters
  const [searchValue, setSearchValue] = useState('');
  const [sortBy, setSortBy] = useState('date');
  const [actionPack, setActionPack] = useState('all');
  
  // State for selections
  const [selectedMetric, setSelectedMetric] = useState('total');
  const [selectedEffortType, setSelectedEffortType] = useState(null);

  // State for dynamic data that can be updated
  const [allTickets, setAllTickets] = useState(TICKET_DETAILS_DATA);
  const [effortTypes] = useState(EFFORT_TYPES_DATA);

  // Calculate metrics dynamically based on current ticket states
  const ticketMetrics = useMemo(() => {
    const metrics = {
      meanTimeToResolution: TICKET_METRICS_DATA.meanTimeToResolution,
      ticketsPending: 0,
      ticketsClosed: 0,
      ticketsDeferred: 0,
      ticketsWorkInProgress: 0
    };

    // Count tickets by status
    allTickets.forEach(ticket => {
      switch (ticket.status) {
        case 'pending':
          metrics.ticketsPending++;
          break;
        case 'closed':
          metrics.ticketsClosed++;
          break;
        case 'deferred':
          metrics.ticketsDeferred++;
          break;
        case 'workInProgress':
          metrics.ticketsWorkInProgress++;
          break;
        default:
          break;
      }
    });

    return metrics;
  }, [allTickets]);

  // Filter tickets based on selections and search
  const filteredTickets = useMemo(() => {
    let filtered = [...allTickets];

    // Filter by selected metric
    if (selectedMetric) {
      switch (selectedMetric) {
        case 'pending':
          filtered = filtered.filter(ticket => ticket.status === 'pending');
          break;
        case 'closed':
          filtered = filtered.filter(ticket => ticket.status === 'closed');
          break;
        case 'deferred':
          filtered = filtered.filter(ticket => ticket.status === 'deferred');
          break;
        case 'workInProgress':
          filtered = filtered.filter(ticket => ticket.status === 'workInProgress');
          break;
        default:
          // 'total' shows all tickets
          break;
      }
    }

    // Filter by selected effort type
    if (selectedEffortType) {
      filtered = filtered.filter(ticket => ticket.effortType === selectedEffortType);
    }

    // Filter by action pack (system)
    if (actionPack && actionPack !== 'all') {
      filtered = filtered.filter(ticket => ticket.system === actionPack);
    }

    // Filter by search
    if (searchValue) {
      const searchLower = searchValue.toLowerCase();
      filtered = filtered.filter(ticket => 
        ticket.title.toLowerCase().includes(searchLower) ||
        ticket.description.toLowerCase().includes(searchLower) ||
        ticket.id.toLowerCase().includes(searchLower)
      );
    }

    // Sort tickets
    switch (sortBy) {
      case 'effort':
        filtered.sort((a, b) => {
          const effortOrder = { 'high': 3, 'medium': 2, 'low': 1 };
          return effortOrder[b.effortType] - effortOrder[a.effortType];
        });
        break;
      case 'status':
        filtered.sort((a, b) => a.status.localeCompare(b.status));
        break;
      default:
        // Default to ID order (simulating date order)
        filtered.sort((a, b) => b.id.localeCompare(a.id));
        break;
    }

    return filtered;
  }, [allTickets, selectedMetric, selectedEffortType, searchValue, sortBy, actionPack]);

  // Handlers
  const handleMetricSelect = (metricKey) => {
    setSelectedMetric(selectedMetric === metricKey ? null : metricKey);
  };

  const handleEffortTypeSelect = (effortId) => {
    setSelectedEffortType(selectedEffortType === effortId ? null : effortId);
  };

  const handleAccept = (ticketId) => {
    console.log('Accept ticket:', ticketId);
    
    // Update ticket status to "Work In Progress"
    setAllTickets(prevTickets => 
      prevTickets.map(ticket => 
        ticket.id === ticketId 
          ? { 
              ...ticket, 
              status: 'workInProgress',
              statusType: 'WORK IN PROGRESS'
            }
          : ticket
      )
    );
  };

  const handleIgnore = (ticketId) => {
    console.log('Ignore ticket:', ticketId);
    
    // Update ticket status to "deferred"
    setAllTickets(prevTickets => 
      prevTickets.map(ticket => 
        ticket.id === ticketId 
          ? { 
              ...ticket, 
              status: 'deferred',
              statusType: 'DEFERRED'
            }
          : ticket
      )
    );
  };

  return (
    <div className={styles.container}>
      <RecommendationHeader
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        sortBy={sortBy}
        onSortChange={setSortBy}
        actionPack={actionPack}
        onActionPackChange={setActionPack}
        sortOptions={FILTER_OPTIONS.sortBy}
        actionPackOptions={FILTER_OPTIONS.actionPack}
        notificationCount={3}
      />

      <TicketMetricsSection
        metrics={ticketMetrics}
        selectedMetric={selectedMetric}
        onMetricSelect={handleMetricSelect}
      />

      <div className={styles.scrollableContent}>
        <EffortTypesSection
          effortTypes={effortTypes}
          selectedEffortType={selectedEffortType}
          onEffortTypeSelect={handleEffortTypeSelect}
        />

        <TicketDetailsSection
          tickets={filteredTickets}
          onAccept={handleAccept}
          onIgnore={handleIgnore}
        />
      </div>
    </div>
  );
};

export default RecommendationCenter;